import React, { useState, useEffect, useRef, useMemo } from 'react'
import { connect } from 'react-redux'
import moment from 'moment'
import theme from '../../../styled/theme'
import { Section, SectionHeader, SectionTitle } from '../../../styled/sections'
import { Text2 } from '../../../components/ui/text'
import { Button } from '../../../components/ui/button'
import Badge from '../../../components/ui/badge'
import Filter from '../../../assets/icons/filter'
import ModalFilter from '../../../components/Modal/ModalFilter'
import {
	resetPrescriptionsFilters,
	setPrescriptionsFilters
} from '../../../actions/filterPrescriptions'
import { addFlashMessage, showLoader, hideLoader } from '../../../actions'
import {
	selectPrescriptionsFilters,
	selectPrescriptionsFiltersCount
} from '../../../reducers/filterPrescriptions'
import Pagination from '../../../components/pagination'
import { usePrescriptionsData } from '../../../hooks/usePrescriptionsData'
import { useSpecialties } from '../../../hooks/useSpecialties'
import { capitalizeEachWord } from '../../../utils/stringsUtils'
import {
	removeAccents,
	buildSpecialtiesWithExtras,
	getSpecialtiesFromList,
	getSpecialtyName
} from '../../../utils/specialties'
import DataTable from '../../../components/DataTable'

const PRESCRIPTION_SORT_OPTIONS = [
	{ id: 'fecha', label: 'Fecha' },
	{ id: 'especialidad', label: 'Especialidad' }
]

const formatLongDate = value => {
	const date = moment(value, ['DD/MM/YYYY', moment.ISO_8601], true)
	return date.isValid() ? date.locale('es').format('D [de] MMMM YYYY') : value
}

function Prescriptions({
	filtersCount,
	filters,
	setPrescriptionsFilters,
	patient,
	auth,
	showLoader,
	hideLoader,
	addFlashMessage
}) {
	const { data, totalPages, currentPage, loading, error, fetchData } = usePrescriptionsData()
	const patientObraSocialId = patient?.idObraSocial || patient?.obraSocial?.id
	const patientPlanId = patient?.idPlan || patient?.plan?.id
	const initialFetchDoneRef = useRef(false)
	const { specialties } = useSpecialties({
		patientObraSocialId,
		patientPlanId,
		showLoader,
		hideLoader,
		addFlashMessage
	})

	useEffect(() => {
		if (initialFetchDoneRef.current || !patient?.pacienteERID) return
		initialFetchDoneRef.current = true
		fetchData({ pagina: 1, itemsPorPagina: 10, orden: 'asc' })
	}, [patient?.pacienteERID, fetchData])

	const specialtiesWithExtras = useMemo(
		() => buildSpecialtiesWithExtras(specialties, data),
		[specialties, data]
	)

	const prescriptionList = data?.map(item => ({
		...item,
		profesional: capitalizeEachWord(item.profesional),
		especialidad: getSpecialtiesFromList(item.especialidad, specialtiesWithExtras)
	}))
	const [showModal, setShowModal] = useState(false)
	const hasActiveFilters = Boolean(
		filters?.especialidad || filters?.fechaDesde || filters?.fechaHasta || filters?.filtro
	)

	const prescriptionFilterFields = [
		{
			type: 'daterange',
			name: 'fecha',
			startField: 'fechaDesde',
			endField: 'fechaHasta',
			label: 'Filtrar por fecha',
			placeholder: 'Ingresá un rango de fechas'
		},
		{
			type: 'autocomplete',
			name: 'especialidad',
			label: 'Filtrar por especialidad',
			placeholder: 'Seleccioná una especialidad',
			options: specialtiesWithExtras
		}
	]

	const handleFilters = () => {
		setShowModal(true)
	}

	const handlePageChange = page => {
		const newFilters = { ...filters, currentPage: page }
		setPrescriptionsFilters(newFilters)
		fetchData({
			especialidad: removeAccents(getSpecialtyName(newFilters.especialidad, specialtiesWithExtras)),
			fechaDesde: newFilters.fechaDesde,
			fechaHasta: newFilters.fechaHasta,
			filtro: newFilters.filtro,
			criterioOrden: newFilters.criterioOrden,
			orden: newFilters.orden || 'asc',
			pagina: page,
			itemsPorPagina: newFilters.itemsPerPage || 10
		})
	}

	return (
		<div style={{ width: '100%' }}>
			<Section gap="32px">
				<SectionHeader align={'flex-start'} gap="8px">
					<SectionTitle mbDesktop="0px">Ordenes</SectionTitle>
					<Text2 color={theme.colors.text.secondaryText}>
						Descargá tus indicaciones médicas en un solo lugar.
					</Text2>
				</SectionHeader>
				<SectionHeader align={'flex-start'}>
					<Button
						color={theme.colors.text.secondaryText}
						$variant={'cardNav'}
						iconLeft={<Filter />}
						label={`Filtros ${filtersCount > 0 ? `(${filtersCount})` : ''}`}
						onClick={handleFilters}
					/>
				</SectionHeader>
			</Section>
			<ModalFilter
				sortOptions={PRESCRIPTION_SORT_OPTIONS}
				filterFields={prescriptionFilterFields}
				showModal={showModal}
				stickyButtonsMobile
				keepDraftOnClose
				onClose={() => setShowModal(false)}
				initialFilters={{ ...filters, sortBy: filters.criterioOrden }}
				onApplyFilters={({ sortBy, fechaDesde, fechaHasta, ...values }) => {
					const newFilters = {
						...filters,
						...values,
						fechaDesde: fechaDesde ? moment(fechaDesde).format('DD/MM/YYYY') : null,
						fechaHasta: fechaHasta ? moment(fechaHasta).format('DD/MM/YYYY') : null,
						criterioOrden: sortBy,
						orden: filters.orden || 'asc',
						currentPage: 1
					}
					setPrescriptionsFilters(newFilters)
					fetchData({
						especialidad: removeAccents(getSpecialtyName(newFilters.especialidad, specialtiesWithExtras)),
						fechaDesde: newFilters.fechaDesde,
						fechaHasta: newFilters.fechaHasta,
						filtro: newFilters.filtro,
						criterioOrden: newFilters.criterioOrden,
						orden: newFilters.orden || 'asc',
						pagina: 1,
						itemsPorPagina: newFilters.itemsPerPage || 10
					})
					setShowModal(false)
				}}
			/>

			<Section>
				<DataTable
					isCarousel={false}
					hasActiveFilters={hasActiveFilters}
					loading={loading}
					error={error}
					paginatedResults={prescriptionList}
					isHome={false}
					showLabels={false}
					emptyTitle="No tenés ordenes"
					emptySubtitle="Cuando tengas una nueva la verás aquí."
					fields={[
						{
							label: 'Fecha',
							key: 'fecha',
							width: '1.2fr',
							mobileHeader: 'right',
							renderMobile: row => (
								<Text2 color={theme.colors.text.secondaryText}>{formatLongDate(row.fecha)}</Text2>
							)
						},
						{ label: 'Especialidad', key: 'especialidad', bold: true, width: '1.4fr' },
						{ label: 'Profesional', key: 'profesional', width: '1.4fr' },
						{ label: 'Clínica', key: 'clinica', width: '1.4fr' },
						{ key: 'info', width: '0.6fr' }
					]}
				/>
			</Section>
			{totalPages?.length > 1 && (
				<Pagination
					totalPages={totalPages}
					currentPage={currentPage}
					onPageChange={handlePageChange}
				/>
			)}
		</div>
	)
}

const mapStateToProps = ({ patient, filterPrescriptions, auth }) => ({
	filters: selectPrescriptionsFilters(filterPrescriptions),
	filtersCount: selectPrescriptionsFiltersCount(filterPrescriptions),
	patient,
	auth
})

const mapDispatchToProps = {
	setPrescriptionsFilters,
	resetPrescriptionsFilters,
	showLoader,
	hideLoader,
	addFlashMessage
}

export default connect(mapStateToProps, mapDispatchToProps)(Prescriptions)
